// src/pages/DocumentList.jsx
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import Layout from '../components/Layout';
import SelectKeyModal from '../components/modals/SelectKeyModal';
import AdvSearchModal from '../components/modals/AdvSearchModal';
import { documentsApi, filtersApi } from '../services/api';
import { useTexts, TXT } from '../context/TextContext';
import { useToast } from '../context/ToastContext';

const PAGE_SIZE = 100;

// ─── Vista Calendario ────────────────────────────────────────────────────────
function CalendarView({ cards }) {
  const navigate = useNavigate();
  const [monthOffset, setMonthOffset] = useState(0);

  const today = new Date();
  const baseDate = new Date(today.getFullYear(), today.getMonth() + monthOffset, 1);
  const year  = baseDate.getFullYear();
  const month = baseDate.getMonth();

  const monthName = baseDate.toLocaleString('default', { month: 'long', year: 'numeric' });

  // Build a map: 'YYYY-MM-DD' → [doc, ...]
  const byDay = {};
  cards.forEach(card => {
    const raw = card.fields?.DOC_FECALTA || card.fields?.DOC_FecAlta || '';
    if (!raw) return;
    // raw may be already formatted (dd/mm/yyyy) or ISO — try both
    let d;
    if (/^\d{4}-\d{2}-\d{2}/.test(raw)) {
      d = new Date(raw);
    } else if (/^\d{2}\/\d{2}\/\d{4}/.test(raw)) {
      const [dd, mm, yyyy] = raw.split('/');
      d = new Date(Number(yyyy), Number(mm) - 1, Number(dd));
    } else {
      d = new Date(raw);
    }
    if (isNaN(d)) return;
    if (d.getFullYear() !== year || d.getMonth() !== month) return;
    const key = `${year}-${String(month + 1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
    if (!byDay[key]) byDay[key] = [];
    byDay[key].push(card);
  });

  // Days in month, first weekday (0=Sun)
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const firstWeekday = new Date(year, month, 1).getDay(); // 0=Sun
  const cells = [];
  for (let i = 0; i < firstWeekday; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);
  while (cells.length % 7 !== 0) cells.push(null);

  return (
    <div>
      {/* Nav */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 }}>
        <button className="btn btn-sm btn-outline-secondary" onClick={() => setMonthOffset(o => o - 1)}>
          <i className="bi bi-chevron-left" />
        </button>
        <span style={{ fontWeight: 600, textTransform: 'capitalize', minWidth: 180, textAlign: 'center' }}>
          {monthName}
        </span>
        <button className="btn btn-sm btn-outline-secondary" onClick={() => setMonthOffset(o => o + 1)}>
          <i className="bi bi-chevron-right" />
        </button>
        {monthOffset !== 0 && (
          <button className="btn btn-sm btn-link text-muted p-0" onClick={() => setMonthOffset(0)}>
            Hoy
          </button>
        )}
      </div>

      {/* Grid */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7,1fr)', gap: 2 }}>
        {['Dom','Lun','Mar','Mié','Jue','Vie','Sáb'].map(d => (
          <div key={d} style={{ textAlign: 'center', fontSize: '0.75rem', fontWeight: 600,
            color: '#888', padding: '4px 0', borderBottom: '1px solid #e5e7eb' }}>
            {d}
          </div>
        ))}
        {cells.map((day, idx) => {
          if (!day) return <div key={`e${idx}`} style={{ minHeight: 80 }} />;
          const key = `${year}-${String(month + 1).padStart(2,'0')}-${String(day).padStart(2,'0')}`;
          const docs = byDay[key] || [];
          const isToday = day === today.getDate() && month === today.getMonth() && year === today.getFullYear();
          return (
            <div key={key} style={{
              minHeight: 80, border: '1px solid #e5e7eb', borderRadius: 6, padding: '4px 5px',
              background: isToday ? 'rgba(42,157,143,0.07)' : '#fff',
            }}>
              <div style={{ fontSize: '0.78rem', fontWeight: isToday ? 700 : 400,
                color: isToday ? 'var(--color1,#2a9d8f)' : '#555', marginBottom: 3 }}>
                {day}
              </div>
              {docs.slice(0, 3).map(doc => (
                <div key={doc.docId}
                  onClick={() => navigate(`/documents/${doc.docId}`)}
                  title={doc.fields?.DOC_TITULO || ''}
                  style={{
                    fontSize: '0.7rem', background: 'var(--color1,#2a9d8f)', color: '#fff',
                    borderRadius: 3, padding: '1px 4px', marginBottom: 2,
                    cursor: 'pointer', overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis',
                  }}>
                  {doc.fields?.DOC_TITULO || `#${doc.docId}`}
                </div>
              ))}
              {docs.length > 3 && (
                <div style={{ fontSize: '0.67rem', color: '#888' }}>+{docs.length - 3} más</div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default function DocumentList() {
  const navigate      = useNavigate();
  const location      = useLocation();
  const [searchParams] = useSearchParams();

  const [cards, setCards]                 = useState([]);
  const [total, setTotal]                 = useState(0);
  const [page, setPage]                   = useState(1);
  const [loading, setLoading]             = useState(false);
  const [vista, setVista]                 = useState('card');
  const [availableViews, setAvailableViews] = useState({}); // { [tdo_idtdoc]: { views, default } }
  const [gridColumns, setGridColumns]       = useState({}); // { [tdo_idtdoc]: [{ field, title }] }
  const hasLoadedRef = useRef(false);
  const [filterId, setFilterId]           = useState(null);
  const [searchText, setSearchText]       = useState('');
  const [docTypeFilter, setDocTypeFilter] = useState(null);
  const activeFilter = useRef({ filterId: null, search: '', docTypeFilter: null });
  const [typeSummary, setTypeSummary]     = useState([]);
  const [canCreate, setCanCreate]         = useState(false);
  const [showSelectKey, setShowSelectKey] = useState(false);
  const [showAdvSearch, setShowAdvSearch] = useState(false);
  const [advSearchParams, setAdvSearchParams] = useState({});
  const [advSearchIsReceipts, setAdvSearchIsReceipts] = useState(false);
  const [error, setError]                 = useState(null);

  // Track whether we need to re-populate (page 1) or just fetch next page
  const isFirstLoad = useRef(true);
  const { t } = useTexts();
  const { showToast } = useToast();

  // On mount — load documents, check permissions, and register global viewDetail()
  // Reaccionar al param ?advsearch= aunque el componente ya esté montado
  useEffect(() => {
    const advParam = searchParams.get('advsearch');
    if (advParam === '1') { setAdvSearchIsReceipts(true);  setShowAdvSearch(true); }
    if (advParam === '2') { setAdvSearchIsReceipts(false); setShowAdvSearch(true); }
  }, [searchParams]);

  // The Tdo_Html template calls onclick='viewDetail({DOC_IDDOCUM})'
  // We expose it as a global function that React intercepts
  useEffect(() => {
    window.viewDetail = (docId) => navigate(`/documents/${docId}`, {
      state: { ...activeFilter.current }
    });

    // Si venimos directo a abrir la búsqueda avanzada, no cargar ningún filtro
    // de fondo — el modal va a reemplazar la lista cuando el usuario busque.
    const advParam = searchParams.get('advsearch');
    if (advParam === '1' || advParam === '2') {
      checkCanCreate();
      return () => { delete window.viewDetail; };
    }

    // Restaurar filtro: 1) location.state, 2) sessionStorage, 3) filtro default del grupo
    const navState   = location.state || {};
    const savedRaw   = sessionStorage.getItem('whl_lastFilter');
    const saved      = savedRaw ? JSON.parse(savedRaw) : null;

    // Preferir location.state si tiene filterId, si no usar sessionStorage
    const restored   = (navState.filterId != null) ? navState : (saved || {});
    const initSearch  = restored.search        || '';
    const initDocType = restored.docTypeFilter || null;
    const initAdvParams = restored.advParams   || null;
    if (initSearch)   setSearchText(initSearch);
    if (initDocType)  setDocTypeFilter(initDocType);
    if (initAdvParams) setAdvSearchParams(initAdvParams);

    const initFilter = restored.filterId ?? null;
    if (initFilter !== null) {
      activeFilter.current = restored;
      setFilterId(initFilter);
      loadPage(1, initFilter, initSearch, initDocType, initAdvParams || {});
    } else if (initAdvParams) {
      // Returning from detail after an advanced search — restore advsearch results
      activeFilter.current = restored;
      loadPage(1, null, initSearch, initDocType, initAdvParams);
    } else {
      // Fetch menu to get the group's default filter (filtrodef)
      filtersApi.menu().then(res => {
        // Si el usuario ya buscó o filtró mientras esperábamos, no pisamos su acción
        if (activeFilter.current.search || activeFilter.current.filterId !== null) return;
        const menu = res.data || [];
        const defFilter = menu.length > 0 ? (menu[0].filtrodef || null) : null;
        const defState = { filterId: defFilter, search: '', docTypeFilter: null, advParams: null };
        activeFilter.current = defState;
        saveFilterState(defState);
        if (defFilter) setFilterId(defFilter);
        loadPage(1, defFilter, '', null);
      }).catch(() => {
        if (activeFilter.current.search || activeFilter.current.filterId !== null) return;
        loadPage(1, null, '', null);
      });
    }

    checkCanCreate();
    return () => { delete window.viewDetail; };
  }, []);

  // Persiste el estado activo en sessionStorage para sobrevivir re-mounts
  const saveFilterState = (state) => {
    sessionStorage.setItem('whl_lastFilter', JSON.stringify(state));
  };

  // Helpers para actualizar estado + activeFilter de forma sincrónica
  const applyFilter = (fid, search = '', dtFilter = null, advParams = null) => {
    const state = { filterId: fid, search, docTypeFilter: dtFilter, advParams };
    activeFilter.current = state;
    saveFilterState(state);
    setFilterId(fid);
    setSearchText(search);
    setDocTypeFilter(dtFilter);
    if (advParams !== null) setAdvSearchParams(advParams);
  };

  // Listen for navbar filter clicks
  useEffect(() => {
    const handleFilter = (e) => {
      const { filterId: fid } = e.detail;
      applyFilter(fid, '', null, null);
      loadPage(1, fid, '', null);
    };
    const handleSearch = (e) => {
      const text = e.detail.text || '';
      const newState = { filterId: null, search: text, docTypeFilter: null, advParams: null };
      activeFilter.current = newState;
      saveFilterState(newState);
      setFilterId(null);
      setSearchText(text);
      setDocTypeFilter(null);
      loadPage(1, null, text, null);
    };
    window.addEventListener('whl:filter', handleFilter);
    window.addEventListener('whl:search', handleSearch);
    return () => {
      window.removeEventListener('whl:filter', handleFilter);
      window.removeEventListener('whl:search', handleSearch);
    };
  }, [filterId]);

  // Toast cuando no hay resultados (solo después de una carga real)
  useEffect(() => {
    if (!loading && hasLoadedRef.current && cards.length === 0) {
      showToast(t(TXT.NO_DOCUMENTS_FOUND, 'No se encontraron documentos.'), 'warning');
    }
  }, [loading, cards]);

  const loadPage = async (targetPage, fid, search, dtFilter, advParams = {}) => {
    setLoading(true);
    setError(null);
    try {
      const params = {
        page: targetPage,
        pageSize: PAGE_SIZE,
        ...(fid      && { filterId: fid }),
        ...(search   && { search }),
        ...(dtFilter && { docTypeFilter: dtFilter }),
        ...advParams,
      };
      const res = await documentsApi.list(params);
      const { cards: newCards, total: newTotal, availableViews: newViews, filterDefaultVista, gridColumns: newGridCols } = res.data;

      setCards((prev) => targetPage === 1 ? newCards : [...prev, ...newCards]);
      setTotal(newTotal);
      setPage(targetPage);

      if (targetPage === 1) {
        if (newViews) {
          setAvailableViews(newViews);
          if (filterDefaultVista) {
            setVista(filterDefaultVista);
          } else {
            const firstTdo = Object.keys(newViews)[0];
            if (firstTdo) setVista(newViews[firstTdo].default || 'card');
          }
        }
        if (newGridCols) setGridColumns(newGridCols);
      }

      // Fetch summary after list is populated
      fetchSummary();
    } catch (err) {
      console.error('Failed to load documents:', err);
      showToast(t(TXT.DOCS_LOAD_ERROR, 'No se pudieron cargar los documentos. Verificá la conexión con el servidor.'), 'error');
    } finally {
      setLoading(false);
      hasLoadedRef.current = true;
    }
  };

  const fetchSummary = async () => {
    try {
      const res = await documentsApi.summary();
      setTypeSummary(res.data);
    } catch (err) {
      console.error('Summary error:', err);
    }
  };

  const checkCanCreate = async () => {
    try {
      const res = await documentsApi.canCreate();
      setCanCreate(res.data.canCreate);
    } catch {
      setCanCreate(false);
    }
  };

  const handleLoadMore = () => {
    loadPage(page + 1, filterId, searchText, docTypeFilter);
  };

  const handleTypeFilter = (tdoId) => {
    setDocTypeFilter(tdoId);
    loadPage(1, filterId, searchText, tdoId);
  };

  const handleCardClick = (docId) => {
    navigate(`/documents/${docId}`, { state: { ...activeFilter.current } });
  };

  const closeAdvSearch = () => {
    setShowAdvSearch(false);
    // Limpiar el param de la URL preservando el state para que "Volver" funcione correctamente
    if (searchParams.get('advsearch')) navigate({ pathname: '/documents' }, { replace: true, state: location.state });
  };

  const handleAdvSearch = (params) => {
    applyFilter(null, params.search || '', null, params);
    closeAdvSearch();
    loadPage(1, null, params.search || '', null, params);
  };

  const renderGrid = (card) => {
    const f = card.fields || {};
    const cols = gridColumns[card.tdoId] || gridColumns[Object.keys(gridColumns)[0]] || [];
    const iconClass = card.icon && card.icon.trim() ? card.icon : 'bi-file-earmark-text';
    const bold = card.hasUnread ? { fontWeight: 700 } : {};

    return (
      <tr
        key={card.docId}
        onClick={() => handleCardClick(card.docId)}
        style={{ cursor: 'pointer', ...bold }}
      >
        <td style={{ width: 36, textAlign: 'center', color: 'var(--color1, #3a7d5c)' }}>
          <i className={`bi ${iconClass}`} />
        </td>
        {cols.map(({ field, title }) => {
          const val = f[field] ?? f[field.toLowerCase()] ?? '';
          const plain = String(val).replace(/<[^>]*>/g, '').trim();
          return (
            <td key={field}>{plain || <span className="text-muted">—</span>}</td>
          );
        })}
      </tr>
    );
  };

  const renderCard = (card) => {
    let html = card.html || '';

    // Replace [ ] brackets with < > (mirrors ASP: Replace(html, "[", "<"))
    html = html.replace(/\[/g, '<').replace(/\]/g, '>');

    // Replace {ICONO} placeholder — value is a BI class name (e.g. "bi-card-text")
    const iconClass = card.icon && card.icon.trim() ? card.icon : 'bi-file-earmark-text';
    html = html.replace(/\{ICONO\}/gi, iconClass);

    // Replace all {FIELD_NAME} placeholders
    if (card.fields) {
      Object.entries(card.fields).forEach(([key, value]) => {
        if (value !== null && value !== undefined && value !== '') {
          let displayValue = String(value);
          if (key === 'DOC_TITULO' && displayValue.length < 25) {
            displayValue = displayValue + '<br>';
          }
          html = html.replace(new RegExp(`\\{${key}\\}`, 'gi'), displayValue);
        }
      });
    }

    // Strip any remaining unreplaced {PLACEHOLDERS}
    html = html.replace(/\{[^}]+\}/g, '');

    // Extract outer div's class and inner content so the React wrapper IS the col div
    // This avoids display:contents issues with Bootstrap gutters
    html = html.trim();
    const outerMatch = html.match(/^<div([^>]*)>([\s\S]*)<\/div>$/s);
    const outerClass = outerMatch
      ? (outerMatch[1].match(/class="([^"]*)"/) || [])[1] || ''
      : '';
    const innerHtml = outerMatch ? outerMatch[2] : html;

    return (
      <div key={card.docId} className={outerClass} style={{ display: 'flex', flexDirection: 'column' }}>
        <div
          className={`flex-grow-1 d-flex${card.hasUnread ? ' card-unread' : ''}`}
          dangerouslySetInnerHTML={{ __html: innerHtml }}
        />
      </div>
    );
  };

  const summaryDropdown = typeSummary.length > 0 ? (
      <div className="btn-group" role="group">
        <button
          type="button"
          className="btn btn-light dropdown-toggle"
          data-bs-toggle="dropdown"
          aria-expanded="false"
        >
          Total: ({typeSummary.reduce((s, t) => s + t.Cantidad, 0)})
        </button>
        <ul className="dropdown-menu dropdown-menu-end">
          {typeSummary.map((t) => (
            <li key={t.tdo_idtdoc}>
              <a
                className={`dropdown-item ${docTypeFilter === t.tdo_idtdoc ? 'active' : ''}`}
                href="#"
                onClick={(e) => { e.preventDefault(); handleTypeFilter(t.tdo_idtdoc); }}
              >
                {t.tdo_nombtdoc} ({t.Cantidad})
              </a>
            </li>
          ))}
          {docTypeFilter && (
            <li>
              <a className="dropdown-item text-muted" href="#"
                onClick={(e) => { e.preventDefault(); handleTypeFilter(null); }}>
                — {t(TXT.VIEW_ALL, 'Ver todos')}
              </a>
            </li>
          )}
        </ul>
      </div>
    ) : null;

  // Compute union of all available views across doc types in the current result
  const VIEW_ORDER = ['grid', 'card', 'calendar'];
  const allViews = [...new Set(Object.values(availableViews).flatMap(v => v.views))]
    .sort((a, b) => VIEW_ORDER.indexOf(a) - VIEW_ORDER.indexOf(b));

  const VIEW_META = {
    card:     { icon: 'bi-grid',      title: t(TXT.VIEW_CARDS,    'Cards') },
    grid:     { icon: 'bi-table',     title: t(TXT.VIEW_GRID,     'Grilla') },
    calendar: { icon: 'bi-calendar3', title: t(TXT.VIEW_CALENDAR, 'Calendario') },
  };

  const viewSelector = allViews.length > 1 ? (
    <div className="btn-group ms-3" role="group" aria-label="Cambiar vista">
      {allViews.map(v => (
        <button
          key={v}
          type="button"
          className={`btn btn-sm ${vista === v ? 'btn-secondary' : 'btn-outline-secondary'}`}
          title={(VIEW_META[v] || {}).title || v}
          onClick={() => setVista(v)}
        >
          <i className={`bi ${(VIEW_META[v] || {}).icon || 'bi-grid'}`} />
        </button>
      ))}
    </div>
  ) : null;

  const buttonBar = canCreate ? (
    <button className="button color1" onClick={() => setShowSelectKey(true)}>
      <i className="fa-solid fa-plus fa-1x color1" />
      &nbsp;&nbsp;{t(TXT.NEW, 'Nuevo')}&nbsp;&nbsp;
    </button>
  ) : null;

  const summaryWithViews = (
    <div style={{ display: 'flex', alignItems: 'center' }}>
      {viewSelector}
      {summaryDropdown && <div className="ms-3">{summaryDropdown}</div>}
    </div>
  );

  return (
    <Layout buttonBar={buttonBar} summary={summaryWithViews}>


      {loading && cards.length === 0 && (
        <div className="col-12 text-center mt-5">
          <div className="spinner-border text-secondary" role="status">
            <span className="visually-hidden">Cargando...</span>
          </div>
        </div>
      )}

      {vista === 'grid' ? (
        <div style={{ overflowX: 'auto' }}>
          <table className="doc-grid-table">
            <thead>
              <tr>
                <th style={{ width: 36 }}></th>
                {(Object.values(gridColumns)[0] || []).map(({ field, title }) => (
                  <th key={field}>{title}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {cards.map((card) => renderGrid(card))}
            </tbody>
          </table>
        </div>
      ) : vista === 'calendar' ? (
        <CalendarView cards={cards} />
      ) : (
        <div className="row gy-4 gx-3">
          {cards.map((card) => renderCard(card))}
        </div>
      )}

      {/* Load more — mirrors list.asp btnmre */}
      {!loading && cards.length > 0 && total > page * PAGE_SIZE && (
        <div className="mt-3">
          <button className="button mt-2 mb-2 color1" onClick={handleLoadMore}>
            <i className="fa-solid fa-plus fa-1x color1"></i>
            &nbsp;&nbsp;{t(TXT.LOAD_MORE, 'Más datos')}&nbsp;&nbsp;
          </button>
        </div>
      )}
      {loading && cards.length > 0 && (
        <div className="text-center mt-3">
          <span className="text-muted">{t(TXT.LOADING_MORE, 'Cargando más...')}</span>
        </div>
      )}

      {/* New document modal */}
      <SelectKeyModal
        show={showSelectKey}
        onClose={() => setShowSelectKey(false)}
        onConfirm={({ docTypeId, areaId, privacyId }) => {
          navigate(`/documents/new?docTypeId=${docTypeId}&areaId=${areaId}&privacyId=${privacyId}`);
        }}
      />

      {/* Advanced search modal */}
      <AdvSearchModal
        show={showAdvSearch}
        onClose={closeAdvSearch}
        onSearch={handleAdvSearch}
        isReceipts={advSearchIsReceipts || docTypeFilter === 3 || docTypeFilter === '3'}
      />

    </Layout>
  );
}
